// Zod-схеми для адмін-API. Спільні між apps/api та apps/webapp.
import { z } from 'zod';

// Категорії водійських посвідчень — синхронізовано з User.licenseCategory у Prisma
export const LICENSE_CATEGORIES = [
  'A1', 'A', 'B1', 'B', 'C1', 'C', 'D1', 'D',
  'BE', 'C1E', 'CE', 'D1E', 'DE',
  'T',
] as const;
export const LicenseCategorySchema = z.enum(LICENSE_CATEGORIES);
export type LicenseCategory = z.infer<typeof LicenseCategorySchema>;

// ===== Питання =====

export const AnswerInputSchema = z.object({
  // id опційний — на create його немає, на update він є
  id: z.number().int().positive().optional(),
  textUk: z.string().min(1, 'Текст відповіді не може бути порожнім').max(500),
  isCorrect: z.boolean(),
  order: z.number().int().min(0).max(10),
});
export type AnswerInput = z.infer<typeof AnswerInputSchema>;

export const QuestionCreateSchema = z
  .object({
    categoryId: z.number().int().positive(),
    textUk: z.string().min(1, 'Текст питання не може бути порожнім').max(2000),
    imageUrl: z.string().nullable().optional(),
    explanation: z.string().max(2000).nullable().optional(),
    explanationCasual: z.string().max(2000).nullable().optional(),
    difficulty: z.number().int().min(1).max(5).default(1),
    // Порожній масив = питання для всіх категорій. Інакше — лише для перелічених.
    licenseCategories: z.array(LicenseCategorySchema).default([]),
    answers: z.array(AnswerInputSchema).min(2).max(6),
  })
  .refine((d) => d.answers.filter((a) => a.isCorrect).length === 1, {
    message: 'Має бути рівно одна правильна відповідь',
    path: ['answers'],
  });
export type QuestionCreateInput = z.infer<typeof QuestionCreateSchema>;

export const QuestionUpdateSchema = z
  .object({
    categoryId: z.number().int().positive().optional(),
    textUk: z.string().min(1).max(2000).optional(),
    imageUrl: z.string().nullable().optional(),
    explanation: z.string().max(2000).nullable().optional(),
    explanationCasual: z.string().max(2000).nullable().optional(),
    difficulty: z.number().int().min(1).max(5).optional(),
    isActive: z.boolean().optional(),
    licenseCategories: z.array(LicenseCategorySchema).optional(),
    answers: z.array(AnswerInputSchema).min(2).max(6).optional(),
  })
  .refine(
    (d) => !d.answers || d.answers.filter((a) => a.isCorrect).length === 1,
    { message: 'Має бути рівно одна правильна відповідь', path: ['answers'] },
  );
export type QuestionUpdateInput = z.infer<typeof QuestionUpdateSchema>;

// ===== Категорії =====

export const CategoryCreateSchema = z.object({
  slug: z.string().min(1).max(50).regex(/^[a-z0-9_-]+$/, 'Тільки латиниця в нижньому регістрі, цифри, _ і -'),
  nameUk: z.string().min(1).max(100),
  description: z.string().max(500).nullable().optional(),
  iconUrl: z.string().nullable().optional(),
  order: z.number().int().min(0).default(0),
});
export type CategoryCreateInput = z.infer<typeof CategoryCreateSchema>;

export const CategoryUpdateSchema = CategoryCreateSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type CategoryUpdateInput = z.infer<typeof CategoryUpdateSchema>;
